import { Icon } from "@iconify/react";
import { Input } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Heading from "../../components/Heading/Heading";
import clsx from "clsx";
import { openVehicleModalTracking } from "../../utils/mixpanel";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

/**
 * @typedef {Object} Vehicle
 * @property {string} name - The full name of the vehicle
 * @property {string} short_name - The short name of the vehicle
 * @property {string} image - A link for the image of the vehicle
 * @property {number} seats - The number of seats available in the vehicle
 * @property {boolean} driver - Whether the vehicle has a driver or not
 * @property {boolean} tracking_system - Whether the vehicle has a tracking system or not
 * @property {boolean} wifi - Whether the vehicle has wifi available or not
 * @property {boolean} security_camera - Whether the vehicle has security camera available or not
 */

/**
 * A modal that acts as a controlled input to select a vehicle from a list of provided vehicles
 *
 * @param {Object} props
 * @param {Vehicle[]} props.VehicleTypeWithService - A list of the vehicles
 * @param {string} props.vehicleTypeName - The name of the selected vehicle
 * @param {React.SetStateAction<string>} props.vehicleTypeName - A set state for the selected vehicle name
 * @returns {JSX.Element}
 */

export default function VehicleTypeModal({
  VehicleTypeWithService,
  vehicleTypeName,
  setVehicleTypeName,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const user = useSelector((state) => state.authData.user);
  const {
    t,
    i18n: { language },
  } = useTranslation("global");

  const openModal = () => {
    openVehicleModalTracking({ user: user?.email });
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchText(event.target.value);
  };

  const handleSelectVehicleType = (vehicleName, idx) => {
    if (vehicleTypeName !== vehicleName) {
      setVehicleTypeName(vehicleName, idx);
    }
    setIsModalOpen(false);
  };

  const filteredVehicleTypes = VehicleTypeWithService?.filter((vehicle) =>
    vehicle.name.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <>
      <button
        onClick={openModal}
        className="block w-full bg-background_white text-black text-left rtl:text-right font-medium text-sm px-5 py-2.5 border-b"
        type="button"
      >
        {vehicleTypeName ? vehicleTypeName : t("hero.select_vehicle_text")}
      </button>

      {isModalOpen && (
        <div
          id="extralarge-modal"
          tabIndex="-1"
          className="fixed top-0 left-0 right-0 z-50 flex justify-center items-center w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full bg-black bg-opacity-50"
          dir={language === "ar" ? "rtl" : "ltr"}
        >
          <div className="relative w-full max-w-7xl max-h-full">
            <div className="relative bg-background_white rounded-lg shadow dark:bg-gray-700">
              <div className="flex items-center justify-between p-4 md:p-5 rounded-t dark:border-gray-600">
                <h3 className="text-xl text-text_white font-medium text-gray-900 dark:text-white">
                  Select Your Vehicle Type
                </h3>
                <button
                  onClick={closeModal}
                  type="button"
                  className="text-gray-400 bg-transparent text-black hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                  data-modal-hide="extralarge-modal"
                >
                  <svg
                    className="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                  <span className="sr-only">Close modal</span>
                </button>
              </div>
              <div className="p-4 md:p-5 space-y-4">
                <Input
                  placeholder={t("search_vehicle_text")}
                  onChange={handleSearch}
                  value={searchText}
                  allowClear
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const toSelect = filteredVehicleTypes[0];
                      if (toSelect) {
                        handleSelectVehicleType(
                          toSelect.name,
                          Math.max(
                            VehicleTypeWithService.findIndex(
                              (e) => e.name === toSelect.name
                            ),
                            0
                          )
                        );
                      } else {
                        closeModal();
                      }
                    }
                  }}
                  prefix={
                    <Icon
                      icon="mingcute:search-line"
                      width="24"
                      height="24"
                      style={{ color: "#E9B838" }}
                    />
                  }
                  className="p-3 bg-background_grey"
                />
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredVehicleTypes?.map((vehicle, index) => (
                      <button
                        key={vehicle.name}
                        onClick={() =>
                          handleSelectVehicleType(vehicle.name, index)
                        } // Handle vehicle type selection
                        className={
                          "group h-auto w-full md:mx-2 mt-2 rounded-lg px-6 py-10 transition-all duration-300 bg-background_grey hover:bg-background_steel_blue"
                        }
                        style={{ cursor: "pointer" }} // Add pointer cursor for clickable effect
                      >
                        <div>
                          <Heading
                            title={`${vehicle.name}`}
                            className={
                              "text-lg transition-all duration-300 text-black group-hover:text-text_white text-start"
                            }
                          />
                        </div>
                        <div
                          className={
                            "text-gray-200 text-sm group-hover:text-text_white text-text_black text-start"
                          }
                        >
                          {vehicle.short_name}
                        </div>
                        <div className="w-full flex flex-row justify-center items-center">
                          <img
                            src={vehicle.image}
                            alt={vehicle.image}
                            style={{ width: "210px", height: "107px" }}
                          />
                        </div>
                        <hr />

                        <div className="mt-3 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          <div
                            className={
                              "p-2 flex flex-col justify-center items-center rounded-md text-gray-200 text-center text-sm bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                            }
                          >
                            {t("vehicle_type.vehicle_data.available_text")}
                            <div className="flex flex-row justify-center items-center">
                              <div>
                                <Icon
                                  icon="mdi:user"
                                  width="1.2em"
                                  height="1.2em"
                                  className={
                                    "bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                  }
                                />
                              </div>
                              <div
                                className={
                                  "mr-2 bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                }
                              >
                                {vehicle.seats}
                              </div>
                            </div>
                          </div>

                          <div
                            className={
                              "p-2 flex flex-col justify-center items-center rounded-md text-gray-200 text-center text-sm bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                            }
                          >
                            {t("vehicle_type.vehicle_data.luggage_text")}
                            <div className="flex flex-row justify-center items-center">
                              <div>
                                <Icon
                                  icon="iconamoon:shopping-bag-thin"
                                  width="1.2em"
                                  height="1.2em"
                                  className={
                                    "bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                  }
                                />
                              </div>
                              <div
                                className={
                                  "mr-2 bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                }
                              >
                                {vehicle.luggage}
                              </div>
                            </div>
                          </div>

                          <div
                            className={clsx(
                              "p-2 flex flex-col justify-center items-center rounded-md text-gray-200 text-center text-sm ",
                              vehicle.driver
                                ? "bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                : "bg-bg_light_gray text-text_white"
                            )}
                          >
                            {vehicle.driver
                              ? t("vehicle_type.vehicle_data.driver_text")
                              : t("vehicle_type.vehicle_data.no_driver_text")}
                            <div className="flex flex-row justify-center items-center">
                              <div>
                                <Icon
                                  icon="healthicons:truck-driver"
                                  width="1.2em"
                                  height="1.2em"
                                  className={
                                    vehicle.driver
                                      ? "bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                      : "bg-bg_light_gray text-text_white"
                                  }
                                />
                              </div>
                              <div
                                className={
                                  "mr-2 bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                }
                              >
                                {vehicle.driver}
                              </div>
                            </div>
                          </div>

                          <div
                            className={clsx(
                              "p-2 flex flex-col justify-center items-center rounded-md text-gray-200 text-center text-sm",
                              vehicle.wifi
                                ? "bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                : "bg-bg_light_gray text-text_white"
                            )}
                          >
                            {vehicle.wifi
                              ? t("vehicle_type.vehicle_data.wifi_text")
                              : t("vehicle_type.vehicle_data.no_wifi_text")}
                            <div className="flex flex-row justify-center items-center">
                              <div>
                                <Icon
                                  icon={
                                    vehicle.wifi
                                      ? "material-symbols:wifi-sharp"
                                      : "clarity:no-wifi-solid"
                                  }
                                  width="1.2em"
                                  height="1.2em"
                                  className={
                                    vehicle.wifi
                                      ? "bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                      : "bg-bg_light_gray text-text_white"
                                  }
                                />
                              </div>
                              <div
                                className={
                                  "mr-2 bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                }
                              >
                                {vehicle.wifi}
                              </div>
                            </div>
                          </div>

                          <div
                            className={clsx(
                              "p-2 flex flex-col justify-center items-center rounded-md text-gray-200 text-center text-sm",
                              vehicle.tracking_system
                                ? "bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                : "bg-bg_light_gray text-text_white"
                            )}
                          >
                            {vehicle.tracking_system
                              ? t("vehicle_type.vehicle_data.tracking_text")
                              : t("vehicle_type.vehicle_data.no_tracking_text")}
                            <div className="flex flex-row justify-center items-center">
                              <div>
                                <Icon
                                  icon="streamline:navigation-arrow-on"
                                  width="1.2em"
                                  height="1.2em"
                                  className={`
                                        ${
                                          vehicle.tracking_system
                                            ? "bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                            : "bg-bg_light_gray text-text_white"
                                        }`}
                                />
                              </div>
                              <div
                                className={
                                  "mr-2 bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                }
                              >
                                {vehicle.tracking_system}
                              </div>
                            </div>
                          </div>

                          <div
                            className={`p-2 flex flex-col justify-center items-center rounded-md text-gray-200 text-center text-sm 
                                   ${
                                     vehicle.security_camera
                                       ? "bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                       : "bg-bg_light_gray text-text_white"
                                   }
                                `}
                          >
                            {vehicle.security_camera
                              ? t("vehicle_type.vehicle_data.security_text")
                              : t("vehicle_type.vehicle_data.no_security_text")}
                            <div className="flex flex-row justify-center items-center">
                              <div>
                                <Icon
                                  icon="ph:security-camera-thin"
                                  width="1.2em"
                                  height="1.2em"
                                  className={`
                                        ${
                                          vehicle.security_camera
                                            ? "bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                            : "bg-bg_light_gray text-text_white"
                                        }`}
                                />
                              </div>
                              <div
                                className={
                                  "mr-2 bg-background_steel_blue text-text_white group-hover:bg-text_white group-hover:text-background_steel_blue"
                                }
                              >
                                {vehicle.security_camera}
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



VehicleTypeModal.propTypes = {
  VehicleTypeWithService: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      short_name: PropTypes.string,
      image: PropTypes.string,
      seats: PropTypes.number,
      driver: PropTypes.bool,
      tracking_system: PropTypes.bool,
      wifi: PropTypes.bool,
      security_camera: PropTypes.bool,
    })
  ).isRequired,
  vehicleTypeName: PropTypes.string.isRequired,
  setVehicleTypeName: PropTypes.func.isRequired,
};
